import React, { useEffect, useState } from "react";
import {
  FaExclamationCircle,
  FaInfoCircle,
  FaComments,
  FaMicrophone,
  FaFileAudio,
  FaLanguage,
  FaListAlt,
  FaKey,
} from "react-icons/fa";
import "../index.css";
import { backendURL } from "../../../config/constants";
import AllowedDomains from "../AllowedDomains";
import SupportedLanguagesTable from "../SupportedLanguagesTable";
import {
  TEXT_SERVER,
  TEXT_BROWSER,
  AUDIO_SERVER,
  AUDIO_BROWSER,
} from "./generateResponseExamples";

const AgentConversationalResponse = () => {
  const [showResponse, setShowResponse] = useState(true);
  const [showError, setShowError] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // In a real app, you would show a toast or notification here
  };

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${backendURL}/agents/supported-languages/`);
        if (!res.ok) throw new Error(`HTTP Error: ${res.status}`);
        const data = await res.json();

        // Expected format: { details: [{ code: "eng", name: "English" }, ...] }
        if (Array.isArray(data.details)) {
          setLanguages(data.details);
        } else {
          setLanguages([]);
        }
      } catch (err) {
        console.error("Error fetching languages:", err);
        setError("Failed to load supported languages.");
      } finally {
        setLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  return (
    <div className="docs-page">
      <div className="docs-page-header">
        <h1 className="docs-page-title">Generate Conversational Response</h1>
        <p className="docs-page-description">
          This endpoint accepts a text query or an audio file, language,
          retrieves relevant context from your knowledge base, and generates a
          sales-oriented, conversational response in the requested language
          using advanced language models.
        </p>
      </div>

      <div className="docs-section">
        <div className="docs-endpoint">
          <div className="docs-endpoint-header">
            <span className="docs-endpoint-method post">POST</span>
            <code className="docs-endpoint-url">
              /api/agents/generate-response/
            </code>
          </div>

          <div className="docs-endpoint-meta">
            <span className="docs-endpoint-auth">
              <FaInfoCircle /> Authentication Required
            </span>
          </div>

          <h3 className="docs-subsection-title">Description</h3>
          <p className="docs-endpoint-description">
            This endpoint provides AI-generated conversational responses to
            customer inquiries. It can accept either text input or audio files
            with language parameter. The system retrieves relevant product
            information from the specified domain, and then generates a natural,
            sales-oriented response in the requested language using advanced
            language models.
          </p>

          <div className="docs-alert docs-alert-info">
            <FaInfoCircle className="docs-alert-icon" />
            <div className="docs-alert-content">
              <div className="docs-alert-title">Multilingual Support</div>
              <p className="docs-alert-message">
                While the system is optimized for{" "}
                <span className="supported-lang">
                  {loading ? (
                    <span>Loading...</span>
                  ) : error ? (
                    <span className="error-text">{error}</span>
                  ) : languages.length > 0 ? (
                    languages.map((lang, index) => (
                      <span key={lang.code}>
                        {lang.name}
                        {index < languages.length - 1 && ", "}
                      </span>
                    ))
                  ) : (
                    <span>
                      English, Italian, French, Spanish, German, Portuguese,
                      Arabic, Chinese (Simplified), Polish
                    </span>
                  )}
                </span>
                responses, it can understand queries in other languages and
                translate them internally. However, the responses will always be
                generated in the detected or preferred language to maintain
                consistency for your multilingual customer base.
              </p>

              <AllowedDomains />
            </div>
          </div>

          <h3 className="docs-subsection-title">Request</h3>
          <p>
            This endpoint accepts <code>multipart/form-data</code> only (for
            both text and audio requests). JSON bodies are not accepted.
          </p>

          <div className="docs-table">
            <h4 className="docs-response-title">Input Parameters</h4>
            <table>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>domain</td>
                  <td>
                    <code>string</code>
                  </td>
                  <td>
                    <span className="docs-badge docs-badge-required">
                      Required
                    </span>
                  </td>
                  <td>One of the domain values listed above</td>
                </tr>
                <tr>
                  <td>lang</td>
                  <td>
                    <code>string</code>
                  </td>
                  <td>
                    <span className="docs-badge docs-badge-optional">
                      Optional*
                    </span>
                  </td>
                  <td>
                    One of the nine codes in the table below (default{" "}
                    <code>eng</code>). English is <code>eng</code>;{" "}
                    For example <code>"en"</code> is not accepted here.
                  </td>
                </tr>
                <tr>
                  <td>text_query</td>
                  <td>
                    <code>string</code>
                  </td>
                  <td>
                    <span className="docs-badge docs-badge-optional">
                      Optional*
                    </span>
                  </td>
                  <td>Text query from user</td>
                </tr>
                <tr>
                  <td>audio_file</td>
                  <td>
                    <code>file</code>
                  </td>
                  <td>
                    <span className="docs-badge docs-badge-optional">
                      Optional*
                    </span>
                  </td>
                  <td>Audio file containing user query</td>
                </tr>
              </tbody>
            </table>
          </div>

          <SupportedLanguagesTable />

          <div className="docs-alert docs-alert-warning">
            <FaExclamationCircle className="docs-alert-icon" />
            <div className="docs-alert-content">
              <div className="docs-alert-title">Required Input</div>
              <p className="docs-alert-message">
                Send exactly one of <code>text_query</code> or{" "}
                <code>audio_file</code>. Sending both, or neither, returns 400.
              </p>
            </div>
          </div>

          <div className="docs-tabs">
            <div className="docs-tabs-header">
              <button className="docs-tab-button active">
                Example Requests
              </button>
            </div>

            <div className="docs-tabs-content">
              <div className="docs-tab-pane active">
                <h4 className="docs-response-title">Text Query Example</h4>
                <div className="docs-code">
                  <div className="docs-code-header">
                    <span>Text Query Request</span>
                    <button
                      className="docs-code-copy"
                      onClick={() =>
                        //
                        copyToClipboard(`curl -X POST https://saas.todoai.ai/api/agents/generate-response/ \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -F "domain=YOUR_DOMAIN" \\
  -F "lang=eng" \\
  -F "text_query=What products do you offer?"`)
                      }
                    >
                      Copy
                    </button>
                  </div>
                  <pre>
                    {`curl -X POST https://saas.todoai.ai/api/agents/generate-response/ \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -F "domain=YOUR_DOMAIN" \\
  -F "lang=eng" \\
  -F "text_query=What products do you offer?"`}
                  </pre>
                </div>

                <h4 className="docs-response-title">Audio File Example</h4>
                <div className="docs-code">
                  <div className="docs-code-header">
                    <span>Audio File Request</span>
                    <button
                      className="docs-code-copy"
                      onClick={() =>
                        copyToClipboard(`curl -X POST https://saas.todoai.ai/api/agents/generate-response/ \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -F "domain=YOUR_DOMAIN" \\
  -F "lang=eng" \\
  -F "audio_file=@/path/to/your/audio.wav"`)
                      }
                    >
                      Copy
                    </button>
                  </div>
                  <pre>
                    {`curl -X POST https://saas.todoai.ai/api/agents/generate-response/ \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -F "domain=YOUR_DOMAIN" \\
  -F "lang=eng" \\
  -F "audio_file=@/path/to/your/audio.wav"`}
                  </pre>
                </div>
              </div>
            </div>
          </div>

          <h3 className="docs-subsection-title">Response</h3>

          <div className="docs-tabs">
            <div className="docs-tabs-header">
              <button
                className={`docs-tab-button ${showResponse ? "active" : ""}`}
                onClick={() => {
                  setShowResponse(true);
                  setShowError(false);
                }}
              >
                Success Response
              </button>
              <button
                className={`docs-tab-button ${showError ? "active" : ""}`}
                onClick={() => {
                  setShowResponse(false);
                  setShowError(true);
                }}
              >
                Error Response
              </button>
            </div>

            <div className="docs-tabs-content">
              {showResponse && (
                <div className="docs-tab-pane active">
                  <h4 className="docs-response-title">Response Fields</h4>
                  <div className="docs-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Type</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>response</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>AI-generated response text</td>
                        </tr>
                        <tr>
                          <td>metadata.query</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>Original user query</td>
                        </tr>
                        <tr>
                          <td>metadata.documents_used</td>
                          <td>
                            <code>integer</code>
                          </td>
                          <td>Number of knowledge base documents referenced</td>
                        </tr>
                        <tr>
                          <td>metadata.context_length</td>
                          <td>
                            <code>integer</code>
                          </td>
                          <td>Total context length used</td>
                        </tr>
                        <tr>
                          <td>metadata.domain</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>Domain identifier</td>
                        </tr>
                        <tr>
                          <td>metadata.user_id</td>
                          <td>
                            <code>integer</code>
                          </td>
                          <td>User ID</td>
                        </tr>
                        <tr>
                          <td>metadata.interaction_timestamp</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>ISO 8601 timestamp</td>
                        </tr>
                        <tr>
                          <td>metadata.language</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>Language code used for the answer (the lang you sent)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h4 className="docs-response-title">metadata Object</h4>
                  <div className="docs-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Type</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>query</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>Final query used</td>
                        </tr>
                        <tr>
                          <td>language</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>
                            Language code used for the answer (the{" "}
                            <code>lang</code> you sent): eng, it, fr, es, de,
                            pt, ar, zh-CN or pl. The key is{" "}
                            <code>language</code>, not <code>lang</code>.
                          </td>
                        </tr>
                        <tr>
                          <td>documents_used</td>
                          <td>
                            <code>int</code>
                          </td>
                          <td>Number of docs retrieved</td>
                        </tr>
                        <tr>
                          <td>domain</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>Domain/category</td>
                        </tr>
                        <tr>
                          <td>user_id</td>
                          <td>
                            <code>int</code>
                          </td>
                          <td>User ID</td>
                        </tr>
                        <tr>
                          <td>interaction_timestamp</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>ISO timestamp</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h4 className="docs-response-title">documents Array</h4>
                  <p>
                    Each item in the documents array is a text string: one
                    matching row from your knowledge base, with its column
                    names and values. Up to five of the most relevant rows are
                    returned, and the array is empty when nothing matched.
                  </p>

                  <div className="docs-code">
                    <div className="docs-code-header">
                      <span>Example Response</span>
                      <button
                        className="docs-code-copy"
                        onClick={() =>
                          copyToClipboard(`{
  "response": "Sure! Our Standard Plan is a great fit for your needs...",
  "metadata": {
    "query": "Which plan fits a small team?",
    "documents_used": 1,
    "context_length": 1450,
    "interaction_timestamp": "2025-05-01T15:00:00.000000",
    "language": "eng",
    "domain": "YOUR_DOMAIN",
    "user_id": 123
  },
  "documents": [
    "Name: Standard Plan\\nSeats: 5\\nSupport: email"
  ]
}`)
                        }
                      >
                        Copy
                      </button>
                    </div>
                    <pre>
                      {`{
  "response": "Sure! Our Standard Plan is a great fit for your needs...",
  "metadata": {
    "query": "Which plan fits a small team?",
    "documents_used": 1,
    "context_length": 1450,
    "interaction_timestamp": "2025-05-01T15:00:00.000000",
    "language": "eng",
    "domain": "YOUR_DOMAIN",
    "user_id": 123
  },
  "documents": [
    "Name: Standard Plan\\nSeats: 5\\nSupport: email"
  ]
}`}
                    </pre>
                  </div>
                </div>
              )}

              {showError && (
                <div className="docs-tab-pane active">
                  <h4 className="docs-response-title">Error Responses</h4>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        400: Invalid language parameter
                      </div>
                      <p className="docs-alert-message">
                        lang must be one of: eng, it, fr, es, de, pt, ar, zh-CN, pl
                      </p>
                    </div>
                  </div>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">400: Missing Input</div>
                      <p className="docs-alert-message">
                        Must provide either text query or audio_file
                      </p>
                    </div>
                  </div>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        400: Both Input provided
                      </div>
                      <p className="docs-alert-message">
                        Cannot provide both text_query and audio_file
                      </p>
                    </div>
                  </div>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        400: unable to transcript audio
                      </div>
                      <p className="docs-alert-message">
                        Audio file could not be processed
                      </p>
                    </div>
                  </div>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        401: Authentication Failed
                      </div>
                      <p className="docs-alert-message">
                        Invalid API key or signature
                      </p>
                    </div>
                  </div>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        500: Internal server error
                      </div>
                      <p className="docs-alert-message">
                        Server Side processing error
                      </p>
                    </div>
                  </div>

                  <div className="docs-code">
                    <div className="docs-code-header">
                      <span>Error Response Example</span>
                      <button
                        className="docs-code-copy"
                        onClick={() =>
                          copyToClipboard(`{
  "error": {
    "non_field_errors": ["Please provide either 'text_query' or 'audio_file'"]
  }
}`)
                        }
                      >
                        Copy
                      </button>
                    </div>
                    <pre>
                      {`{
  "error": {
    "non_field_errors": ["Please provide either 'text_query' or 'audio_file'"]
  }
}`}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaComments /> Implementation Examples
        </h2>

        <div className="docs-alert docs-alert-warning">
          <FaExclamationCircle className="docs-alert-icon" />
          <div className="docs-alert-content">
            <div className="docs-alert-title">Call the API from your backend</div>
            <p className="docs-alert-message">
              Your API key, secret key and signature must stay on your server.
              The browser examples below call your own backend, and the backend
              examples call this API. Display user input and API responses with{" "}
              <code>textContent</code> (or sanitized HTML), never raw{" "}
              <code>innerHTML</code>.
            </p>
          </div>
        </div>

        <div className="docs-card">
          <div className="docs-card-title">
            <FaLanguage /> Text Query Example
          </div>
          <div className="docs-card-content">
            <p>
              The browser sends the question to your backend. Your backend adds
              the credentials and signature, then calls this API:
            </p>

            <div className="docs-code">
              <div className="docs-code-header">
                <span>Your backend (Node.js)</span>
                <button
                  className="docs-code-copy"
                  onClick={() => copyToClipboard(TEXT_SERVER)}
                >
                  Copy
                </button>
              </div>
              <pre>{TEXT_SERVER}</pre>
            </div>

            <div className="docs-code">
              <div className="docs-code-header">
                <span>Browser (calls your backend)</span>
                <button
                  className="docs-code-copy"
                  onClick={() => copyToClipboard(TEXT_BROWSER)}
                >
                  Copy
                </button>
              </div>
              <pre>{TEXT_BROWSER}</pre>
            </div>
          </div>
        </div>

        <div className="docs-card">
          <div className="docs-card-title">
            <FaFileAudio /> Audio File Example
          </div>
          <div className="docs-card-content">
            <p>
              Send the recording in the format the browser produced, with a
              file name that matches it (for example{" "}
              <code>recording.webm</code>). Do not label a recording as WAV
              unless it is a WAV file: changing the file name or the blob's
              MIME type does not convert the audio.
            </p>

            <div className="docs-code">
              <div className="docs-code-header">
                <span>Your backend (Node.js)</span>
                <button
                  className="docs-code-copy"
                  onClick={() => copyToClipboard(AUDIO_SERVER)}
                >
                  Copy
                </button>
              </div>
              <pre>{AUDIO_SERVER}</pre>
            </div>

            <div className="docs-code">
              <div className="docs-code-header">
                <span>Browser (calls your backend)</span>
                <button
                  className="docs-code-copy"
                  onClick={() => copyToClipboard(AUDIO_BROWSER)}
                >
                  Copy
                </button>
              </div>
              <pre>{AUDIO_BROWSER}</pre>
            </div>
          </div>
        </div>

        <div className="docs-card">
          <div className="docs-card-title">
            <FaKey /> Generate Signature Example
          </div>
          <div className="docs-card-content">
            <p>Here's an example of how to make signature key:</p>

            <div className="docs-code">
              <div className="docs-code-header">
                <span>JavaScript Signature Creation Example</span>
                <button
                  className="docs-code-copy"
                  onClick={() =>
                    copyToClipboard(`// Node.js 18+ (built-in fetch and FormData). Run on your server only.
import crypto from "crypto";

// Your API credentials
const API_KEY = process.env.TODOAI_API_KEY;       // pk_YOUR_API_KEY
const SECRET_KEY = process.env.TODOAI_SECRET_KEY; // YOUR_SECRET_KEY

// The API endpoint
const API_URL = "https://saas.todoai.ai/api/agents/generate-response/";

// 1. The signature is the HMAC-SHA256 hex digest of the domain value
const domain = "YOUR_DOMAIN";
const signature = crypto
  .createHmac("sha256", SECRET_KEY)
  .update(domain)
  .digest("hex");

// 2. Build the multipart form (this endpoint does not accept JSON)
const formData = new FormData();
formData.append("domain", domain);
formData.append("lang", "it");
formData.append("text_query", "Where is my order?");

// 3. Send the request with the X-API-Key and X-Signature headers
async function sendRequest() {
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "X-API-Key": API_KEY,
        "X-Signature": signature,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(JSON.stringify(data.error));
    console.log("AI Response:", data.response);
  } catch (error) {
    console.error("Error:", error.message);
  }
}

sendRequest();
`)
                  }
                >
                  Copy
                </button>
              </div>
              <pre>
                {`// Node.js 18+ (built-in fetch and FormData). Run on your server only.
import crypto from "crypto";

// Your API credentials
const API_KEY = process.env.TODOAI_API_KEY;       // pk_YOUR_API_KEY
const SECRET_KEY = process.env.TODOAI_SECRET_KEY; // YOUR_SECRET_KEY

// The API endpoint
const API_URL = "https://saas.todoai.ai/api/agents/generate-response/";

// 1. The signature is the HMAC-SHA256 hex digest of the domain value
const domain = "YOUR_DOMAIN";
const signature = crypto
  .createHmac("sha256", SECRET_KEY)
  .update(domain)
  .digest("hex");

// 2. Build the multipart form (this endpoint does not accept JSON)
const formData = new FormData();
formData.append("domain", domain);
formData.append("lang", "it");
formData.append("text_query", "Where is my order?");

// 3. Send the request with the X-API-Key and X-Signature headers
async function sendRequest() {
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "X-API-Key": API_KEY,
        "X-Signature": signature,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(JSON.stringify(data.error));
    console.log("AI Response:", data.response);
  } catch (error) {
    console.error("Error:", error.message);
  }
}

sendRequest();`}
              </pre>
            </div>
          </div>
        </div>

        <div className="docs-card">
          <div className="docs-card-title">
            <FaListAlt /> Best Practices
          </div>
          <div className="docs-card-content">
            <ul className="docs-list">
              <li>
                <strong>Query Clarity:</strong> For text queries, be as specific
                as possible to get the most relevant responses
              </li>
              <li>
                <strong>Audio Quality:</strong> For audio files, ensure clear
                recording with minimal background noise for best transcription
                results
              </li>
              <li>
                <strong>Error Handling:</strong> Implement robust error handling
                for both client-side validation and API response errors
              </li>
              <li>
                <strong>Response Caching:</strong> Consider caching common
                queries to improve performance for frequently asked questions
              </li>
              <li>
                <strong>User Interface:</strong> Display both the AI response
                and suggested products in a user-friendly manner
              </li>
            </ul>
          </div>
        </div>

        <div className="docs-alert docs-alert-info">
          <FaMicrophone className="docs-alert-icon" />
          <div className="docs-alert-content">
            <div className="docs-alert-title">Audio File Support</div>
            <p className="docs-alert-message">
              Supported audio formats: WAV, WebM, OGG, MP3, MP4/M4A and FLAC.
              Send the file in its original format with a matching file name
              extension; do not rename or relabel it. For best results, keep
              recordings clear, with minimal background noise, and not longer
              than a few minutes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentConversationalResponse;
